import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIELDS } from "@/lib/fields";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, Trash2 } from "lucide-react";

const IMPORTABLE_FIELDS = FIELDS.filter((f) => !f.computed && !f.auto);

export default function ImportPage() {
  const { can } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadBatches = () => api.get("/cases/batches").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => { loadBatches(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const deleteBatch = async (id) => {
    if (!window.confirm("Delete this entire batch? ALL cases imported in it will be permanently deleted.")) return;
    try {
      const r = await api.delete(`/cases/batches/${id}`);
      toast.success(`Batch deleted (${r.data.deleted_cases} cases removed)`);
      loadBatches();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/cases/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      loadBatches();
      toast.success(`Imported ${r.data.created} cases`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="import-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">Excel Import</h1>
        <p className="text-sm text-[#4B5563] mt-1">
          Bulk-load cases from your existing tracker sheet. Column headers are matched to the fields below — download the template for the exact format.
        </p>
      </div>

      <div className="bg-white border border-[#E5E7EB] p-6 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/template`} data-testid="download-template-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] border border-[#2563EB] px-3 py-2 rounded-sm hover:bg-[#2563EB]/5 transition-colors">
            <Download className="h-4 w-4" /> Download CSV Template
          </a>
          <span className="text-xs text-[#9CA3AF]">Accepts .xlsx, .xls and .csv</span>
        </div>

        <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#E5E7EB] hover:border-[#2563EB] transition-colors p-10 cursor-pointer bg-[#F9FAFB]"
          data-testid="import-dropzone">
          <FileSpreadsheet className="h-10 w-10 text-[#9CA3AF] mb-3" />
          <span className="text-sm font-medium text-[#111827]">{file ? file.name : "Click to choose your tracker file"}</span>
          <span className="text-xs text-[#9CA3AF] mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB` : "Headers are matched case-insensitively"}</span>
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
        </label>

        <Button onClick={upload} disabled={!file || uploading}
          className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="import-upload-button">
          <Upload className="h-4 w-4 mr-2" /> {uploading ? "Importing..." : "Import Cases"}
        </Button>

        {result && (
          <div className="border border-[#E5E7EB] p-4 space-y-2" data-testid="import-result">
            <div className="text-sm"><span className="font-bold text-[#10B981]">{result.created}</span> cases created ·{" "}
              <span className="font-bold text-[#F59E0B]">{result.skipped}</span> empty rows skipped ·{" "}
              <span className="font-bold text-[#EF4444]">{result.errors.length}</span> errors · {result.total_rows} rows read</div>
            {result.errors.length > 0 && (
              <ul className="text-xs text-[#EF4444] list-disc pl-5">
                {result.errors.map((err) => <li key={err}>{err}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB]" data-testid="batches-section">
        <div className="p-4 border-b border-[#E5E7EB] text-xs font-bold uppercase tracking-widest text-[#4B5563]">
          Import Batches
        </div>
        {batches.length === 0 ? (
          <div className="p-4 text-sm text-[#9CA3AF]" data-testid="batches-empty">No import batches yet. Batches are created automatically when you upload a file.</div>
        ) : (
          <table className="w-full text-sm" data-testid="batches-table">
            <thead>
              <tr className="border-b border-[#F3F4F6] bg-[#F9FAFB]">
                {["File", "Imported On", "By", "Cases", ""].map((h) => (
                  <th key={h} className="text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-b border-[#F3F4F6] hover:bg-[#F3F4F6] transition-colors" data-testid={`batch-row-${b.id}`}>
                  <td className="py-2 px-3 font-medium">{b.filename}</td>
                  <td className="py-2 px-3 text-[#4B5563]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</td>
                  <td className="py-2 px-3 text-[#4B5563]">{b.imported_by}</td>
                  <td className="py-2 px-3 font-mono text-xs font-bold">{b.case_count}</td>
                  <td className="py-2 px-3">
                    {can("delete_case") && (
                      <Button variant="outline" size="sm"
                        className="rounded-sm border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/5"
                        onClick={() => deleteBatch(b.id)} data-testid={`delete-batch-${b.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Batch
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-[#4B5563] mb-3">Recognized Columns</div>
        <div className="flex flex-wrap gap-1.5">
          {IMPORTABLE_FIELDS.map((f) => (
            <span key={f.key} className="text-[10px] font-mono bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5 rounded-sm">{f.label}</span>
          ))}
        </div>
        <p className="text-xs text-[#9CA3AF] mt-3">OLD DAYS and TAT are computed automatically from Received Date — do not include them in the file.</p>
      </div>
    </div>
  );
}
